import { useState, useEffect } from 'react';
import SEO, { PAGE_SEO } from '../components/SEO';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Play, Sparkles, Clock, Filter } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get SEO-friendly content URL
const getContentUrl = (item) => `/content/${item.slug || item.content_id}`;

// Video categories
const VIDEO_CATEGORIES = [
  { id: 'all', label: 'All Videos' },
  { id: 'tutorials', label: 'Tutorials' },
  { id: 'cosmetics how-to', label: 'Cosmetics How-To' },
  { id: 'unboxing', label: 'Unboxing & Hauls' },
  { id: 'instructions', label: 'How to Use' },
  { id: 'skincare routines', label: 'Skincare Routines' },
  { id: 'makeup tutorials', label: 'Makeup' },
  { id: 'anti-aging', label: 'Anti-Aging' },
  { id: 'k-beauty', label: 'K-Beauty' },
  { id: 'product reviews', label: 'Reviews' },
];

export default function VideoVault() {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/content?content_type=video&limit=100`)
      .then(res => res.json())
      .then(data => {
        setVideos(data);
        setFilteredVideos(data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Filter videos by category
  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredVideos(videos);
    } else {
      const filtered = videos.filter(v => 
        v.category?.toLowerCase().includes(selectedCategory.toLowerCase()) ||
        v.tags?.some(tag => tag.toLowerCase().includes(selectedCategory.toLowerCase()))
      );
      setFilteredVideos(filtered);
    }
  }, [selectedCategory, videos]);

  // Get unique categories from videos
  const availableCategories = [...new Set(videos.map(v => v.category))].filter(Boolean);

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="video-vault-page">
      <SEO {...PAGE_SEO.videoVault} url="/video-vault" />
      <Navbar />
      
      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-4">
              <Play className="w-4 h-4 text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wider text-gold">Videos</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl font-semibold mb-4">Video Vault</h1>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Watch expert tutorials, unboxings, how-tos, and visual guides for your beauty routine.
            </p>
          </div>

          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Filter by category:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {VIDEO_CATEGORIES.map((cat) => (
                <Button
                  key={cat.id}
                  variant={selectedCategory === cat.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={selectedCategory === cat.id ? "bg-gold hover:bg-gold/90 text-black" : ""}
                  data-testid={`filter-${cat.id}`}
                >
                  {cat.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          {!loading && (
            <p className="text-sm text-muted-foreground mb-6">
              Showing {filteredVideos.length} video{filteredVideos.length !== 1 ? 's' : ''}
              {selectedCategory !== 'all' && ` in "${VIDEO_CATEGORIES.find(c => c.id === selectedCategory)?.label}"`}
            </p>
          )}

          {/* Videos Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i} className="border-none">
                  <Skeleton className="aspect-video" />
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-24 mb-4" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-full" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredVideos.length === 0 ? (
            <div className="text-center py-16">
              <Play className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">No videos found in this category.</p>
              <Button 
                variant="outline" 
                onClick={() => setSelectedCategory('all')}
              >
                View All Videos
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVideos.map((item) => (
                <Link 
                  key={item.content_id} 
                  to={getContentUrl(item)}
                  className="group"
                  data-testid={`video-item-${item.content_id}`}
                >
                  <Card className="bg-card border-none shadow-card card-hover h-full overflow-hidden">
                    <div className="aspect-video overflow-hidden relative">
                      <img
                        src={item.image_url || 'https://images.pexels.com/photos/30797176/pexels-photo-30797176.jpeg'}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      {/* Play Button Overlay */}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                        <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg">
                          <Play className="w-6 h-6 text-primary fill-current ml-1" />
                        </div>
                      </div>
                      {/* Duration Badge */}
                      <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/70 text-white text-xs rounded-sm font-medium">
                        {item.read_time}:00
                      </div>
                      {/* Category Badge */}
                      <div className="absolute top-3 left-3 px-2 py-1 bg-gold text-black text-xs rounded-sm font-semibold uppercase tracking-wider">
                        {item.category}
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h2 className="font-serif text-lg font-semibold mb-2 group-hover:text-gold transition-colors line-clamp-2">
                        {item.title}
                      </h2>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{item.summary}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1 px-2 py-1 bg-gold/10 rounded-sm">
                          <Sparkles className="w-3 h-3 text-gold" />
                          <span className="text-xs font-semibold text-gold">+{item.points_reward} pts</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {item.read_time} min
                          </span>
                          <span>{item.views} views</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
